<template>
  <div class="page-container">
    <el-card v-loading="pageLoading">
      <div slot="header" class="page-header">
        <span class="page-title">{{ isEdit ? '编辑产品' : '新增产品' }}</span>
        <div class="page-actions">
          <el-button size="small" @click="handleCancel">取消</el-button>
          <el-button type="primary" size="small" :loading="saving" @click="handleSave">
            {{ isEdit ? '更新' : '保存' }}
          </el-button>
        </div>
      </div>

      <el-form ref="productForm" :model="form" :rules="rules" label-width="130px" class="product-form">

        <el-form-item label="产品号" prop="product_no">
          <el-input
            v-model="form.product_no"
            :disabled="isEdit"
            placeholder="请输入产品号（唯一）"
            class="w-md"
          />
        </el-form-item>

        <el-form-item label="产品名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入产品名称" class="w-md" />
        </el-form-item>

        <el-form-item label="产品略称">
          <el-input v-model="form.short_name" placeholder="请输入产品略称（可选）" class="w-md" />
        </el-form-item>

        <el-form-item label="卖点" prop="selling_points">
          <el-input
            v-model="form.selling_points"
            placeholder="请输入卖点（可选，最多10个字）"
            class="w-md"
            maxlength="10"
            show-word-limit
            clearable
          />
        </el-form-item>

        <!-- 产品大图 -->
        <el-form-item label="产品大图（多个）">
          <div class="image-list">
            <div v-for="(url, idx) in form.images" :key="idx" class="image-box">
              <el-image :src="url" fit="cover" class="image-inner" :preview-src-list="form.images" />
              <div class="image-mask">
                <i class="el-icon-delete" @click="removeImage(idx)" />
              </div>
            </div>
            <el-upload
              action="#"
              accept="image/jpeg,image/png,image/webp,image/gif"
              :show-file-list="false"
              :multiple="true"
              :http-request="handleImageUpload"
            >
              <div class="image-add-box">
                <i v-if="!imageUploading" class="el-icon-plus" />
                <i v-else class="el-icon-loading" />
              </div>
            </el-upload>
          </div>
          <div class="form-tip">支持 JPG/PNG/WebP/GIF，可上传多张</div>
        </el-form-item>

        <!-- 产品缩略图 -->
        <el-form-item label="产品缩略图（1个）">
          <div class="image-list">
            <div v-if="form.thumbnail" class="image-box">
              <el-image :src="form.thumbnail" fit="cover" class="image-inner" :preview-src-list="[form.thumbnail]" />
              <div class="image-mask">
                <i class="el-icon-delete" @click="form.thumbnail = ''" />
              </div>
            </div>
            <el-upload
              v-if="!form.thumbnail"
              action="#"
              accept="image/jpeg,image/png,image/webp"
              :show-file-list="false"
              :http-request="handleThumbUpload"
            >
              <div class="image-add-box">
                <i v-if="!thumbUploading" class="el-icon-plus" />
                <i v-else class="el-icon-loading" />
              </div>
            </el-upload>
          </div>
          <div class="form-tip">建议正方形，支持 JPG/PNG/WebP</div>
        </el-form-item>

        <!-- 文字内容 -->
        <el-form-item label="产品简介">
          <el-input
            v-model="form.intro"
            type="textarea"
            :rows="4"
            placeholder="请输入产品简介（可选）"
            class="w-lg"
          />
        </el-form-item>

        <el-form-item label="产品详情">
          <rich-editor v-model="form.detail" :height="400" placeholder="请输入产品详情内容..." />
        </el-form-item>

        <el-form-item label="购买须知">
          <rich-editor v-model="form.purchase_instructions" :height="400" placeholder="请输入购买须知内容..." />
        </el-form-item>

        <!-- 价格与排序 -->
        <el-form-item label="划线价">
          <el-input-number v-model="form.original_price" :min="0" :precision="2" :step="100" />
          <span class="form-tip ml">元（选填，用于展示划线原价）</span>
        </el-form-item>

        <el-form-item label="实收价" prop="price">
          <el-input-number v-model="form.price" :min="0" :precision="2" :step="100" />
          <span class="form-tip ml">元</span>
        </el-form-item>

        <el-form-item label="显示顺序">
          <el-input-number v-model="form.sort" :min="0" :max="999" />
          <span class="form-tip ml">数值越小越靠前</span>
        </el-form-item>

        <el-form-item label="采样样本">
          <el-checkbox-group v-model="form.sample_types">
            <el-checkbox label="blood">血液</el-checkbox>
            <el-checkbox label="tissue">组织</el-checkbox>
          </el-checkbox-group>
        </el-form-item>

        <el-form-item label="产品状态">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">上架</el-radio>
            <el-radio :label="0">下架</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 底部操作 -->
        <el-form-item>
          <el-button size="small" @click="handleCancel">取消</el-button>
          <el-button type="primary" size="small" :loading="saving" @click="handleSave">
            {{ isEdit ? '更新' : '保存' }}
          </el-button>
        </el-form-item>

      </el-form>
    </el-card>
  </div>
</template>

<script>
import {
  getProductDetail,
  createProduct,
  updateProduct,
  uploadProductImage
} from '@/api/product'

const defaultForm = () => ({
  product_no: '',
  name: '',
  short_name: '',
  selling_points: '',
  images: [],
  thumbnail: '',
  intro: '',
  detail: '',
  original_price: 0,
  price: 0,
  sort: 0,
  status: 1,
  sample_types: [],
  purchase_instructions: ''
})

export default {
  name: 'ProductEdit',
  data() {
    return {
      pageLoading: false,
      saving: false,
      thumbUploading: false,
      imageUploading: false,
      form: defaultForm(),
      rules: {
        product_no: [{ required: true, message: '产品号不能为空', trigger: 'blur' }],
        name:       [{ required: true, message: '产品名称不能为空', trigger: 'blur' }],
        price:      [{ required: true, type: 'number', min: 0, message: '请输入实收价', trigger: 'change' }],
        selling_points: [
          {
            max: 10,
            message: '卖点最多10个字',
            trigger: 'blur'
          }
        ]
      }
    }
  },
  computed: {
    productId() {
      return this.$route.query.id ? Number(this.$route.query.id) : null
    },
    isEdit() {
      return !!this.productId
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(to) {
        if (to.path !== '/product/edit') return
        if (to.query.id) {
          this.fetchDetail()
        } else {
          this.form = defaultForm()
        }
      }
    }
  },
  methods: {
    async fetchDetail() {
      this.pageLoading = true
      try {
        const res = await getProductDetail({ id: this.productId })
        const d = res.data
        this.form = {
          product_no:     d.product_no,
          name:           d.name,
          short_name:     d.short_name || '',
          selling_points: d.selling_points || '',
          images:         d.images ? [...d.images] : [],
          thumbnail:      d.thumbnail || '',
          intro:          d.intro || '',
          detail:         d.detail || '',
          original_price: d.original_price || 0,
          price:          d.price || 0,
          sort:           d.sort || 0,
          status:         d.status,
          sample_types:   d.sample_types ? [...d.sample_types] : [],
          purchase_instructions: d.purchase_instructions || ''
        }
      } finally {
        this.pageLoading = false
      }
    },

    async handleThumbUpload({ file }) {
      this.thumbUploading = true
      try {
        const res = await uploadProductImage(file)
        this.form.thumbnail = res.data.url
        this.$message.success('缩略图上传成功')
      } catch (e) {
        this.$message.error(e.message || '上传失败')
      } finally {
        this.thumbUploading = false
      }
    },

    async handleImageUpload({ file }) {
      this.imageUploading = true
      try {
        const res = await uploadProductImage(file)
        this.form.images.push(res.data.url)
        this.$message.success('图片上传成功')
      } catch (e) {
        this.$message.error(e.message || '上传失败')
      } finally {
        this.imageUploading = false
      }
    },

    removeImage(idx) {
      this.form.images.splice(idx, 1)
    },

    handleSave() {
      this.$refs.productForm.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          const payload = {
            product_no:     this.form.product_no,
            name:           this.form.name,
            short_name:     this.form.short_name || undefined,
            selling_points: (this.form.selling_points && String(this.form.selling_points).trim()) || undefined,
            images:         this.form.images,
            thumbnail:      this.form.thumbnail || undefined,
            intro:          this.form.intro || undefined,
            detail:         this.form.detail || undefined,
            original_price: this.form.original_price || undefined,
            price:          this.form.price,
            sort:           this.form.sort,
            status:         this.form.status,
            sample_types:   this.form.sample_types,
            purchase_instructions: this.form.purchase_instructions || undefined
          }
          if (this.isEdit) {
            await updateProduct({ id: this.productId, ...payload })
            this.$message.success('更新成功')
          } else {
            await createProduct(payload)
            this.$message.success('创建成功')
          }
          this.$router.push('/product/index')
        } finally {
          this.saving = false
        }
      })
    },

    handleCancel() {
      this.$router.push('/product/index')
    }
  }
}
</script>

<style lang="scss" scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .page-title {
    font-size: 15px;
    font-weight: 600;
    color: #303133;
  }

  .page-actions {
    display: flex;
    gap: 8px;
  }
}

.product-form {
  max-width: 860px;
  padding-top: 8px;

  .w-md { width: 340px; }
  .w-lg { width: 560px; }
}

/* 图片上传列表 */
.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-start;
}

.image-box {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #e4e7ed;

  .image-inner {
    width: 100%;
    height: 100%;
    display: block;
  }

  .image-mask {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s;
    gap: 12px;

    i {
      font-size: 20px;
      color: #fff;
      cursor: pointer;

      &.el-icon-delete:hover { color: #f56c6c; }
    }
  }

  &:hover .image-mask {
    opacity: 1;
  }
}

.image-add-box {
  width: 120px;
  height: 120px;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #fafafa;
  transition: border-color 0.2s, background 0.2s;

  i {
    font-size: 28px;
    color: #c0c4cc;
    transition: color 0.2s;
  }

  &:hover {
    border-color: #409eff;
    background: #f0f7ff;
    i { color: #409eff; }
  }

  &.loading i {
    color: #409eff;
    animation: rotating 1s linear infinite;
  }
}

@keyframes rotating {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  display: block;

  &.ml {
    display: inline;
    margin-top: 0;
    margin-left: 8px;
  }
}
</style>
